<!-- frontend/src/views/ActivityDetail.vue -->
<!-- 活動詳情頁面 — 參考 activity_detail.xlsx 版面設計 -->
<template>
  <div class="detail-page" v-if="activity">
    <div class="detail-card">
      <!-- 標題區 -->
      <div class="detail-header">
        <h2>活動線上報名系統</h2>
      </div>

      <!-- 資訊表格 -->
      <table class="detail-table">
        <tbody>
          <!-- 第一行：活動編號 + 主辦單位 -->
          <tr>
            <th>活動編號</th>
            <td>{{ activity.id }}</td>
            <th>主辦單位</th>
            <td>{{ activity.dept_name || activity.dept_id || '—' }}</td>
          </tr>
          <!-- 第二行：承辦人 + 連絡電話 -->
          <tr>
            <th>承辦人</th>
            <td>{{ activity.organizer_name || '—' }}</td>
            <th>連絡電話</th>
            <td>{{ activity.organizer_phone || '—' }}</td>
          </tr>
          <!-- 活動名稱 -->
          <tr>
            <th>活動名稱</th>
            <td colspan="3">{{ activity.title }}</td>
          </tr>
          <!-- 活動時間 -->
          <tr>
            <th>活動時間</th>
            <td colspan="3">{{ formatTimeRange(activity.start_date, activity.end_date) }}</td>
          </tr>
          <!-- 活動地點 -->
          <tr>
            <th>活動地點</th>
            <td colspan="3">{{ activity.location || '—' }}</td>
          </tr>
          <!-- 活動性質 -->
          <tr>
            <th>活動性質</th>
            <td colspan="3">
              {{ formatCategory(activity.category) }}
              <span v-if="activity.speaker">，講者：{{ activity.speaker }}</span>
            </td>
          </tr>
          <!-- 自主學習點數 -->
          <tr>
            <th>自主學習點數</th>
            <td colspan="3">
              {{ activity.points || activity.learning_hours || 0 }} 點
              <span v-if="activity.needs_reflection && activity.reflection_points > 0" class="reflection-bonus">（繳交心得可再獲得 {{ activity.reflection_points }} 點）</span>
            </td>
          </tr>
          <!-- 活動簡述及目標 -->
          <tr v-if="activity.description">
            <th>活動簡述及目標</th>
            <td colspan="3" class="description-cell">
              <div class="rich-content" v-html="safeDescription"></div>
            </td>
          </tr>
          <!-- 活動連結 -->
          <tr v-if="activity.url">
            <th>相關連結</th>
            <td colspan="3">
              <a :href="activity.url" target="_blank" rel="noopener">{{ activity.url }}</a>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 活動公告 -->
      <div class="announcement" v-if="announcement">
        <h3>活動公告</h3>
        <p style="white-space: pre-wrap;">{{ announcement }}</p>
      </div>

      <!-- 報名情形 -->
      <div class="registration-info">
        <h3>報名情形</h3>
        <p v-if="activity.reg_time_start || activity.reg_time_end" class="reg-time">
          報名時間：{{ formatRegTime(activity.reg_time_start) }} ~ {{ formatRegTime(activity.reg_time_end) }}
        </p>
        <p v-if="activity.max_participants">
          本活動接受 <strong>{{ activity.max_participants }}</strong> 人報名，目前已有 <strong>{{ activity.registration_count || 0 }}</strong> 人報名
          <span v-if="isFull" class="full-badge">報名已額滿</span>
        </p>
        <p v-else>
          目前已有 <strong>{{ activity.registration_count || 0 }}</strong> 人報名
        </p>
        <p class="reg-deadline">報名截止：{{ formatDeadline }}<span v-if="isRegistrationClosed" class="full-badge">報名已截止</span></p>
      </div>

      <!-- 報名已額滿 -->
      <div class="register-section" v-if="!isRegistered && isFull">
        <p class="full-text">本活動報名人數已達上限，報名已額滿。</p>
      </div>

      <!-- 報名已截止 -->
      <div class="register-section" v-else-if="!isRegistered && isRegistrationClosed">
        <p class="full-text">已超過報名截止時間，報名已截止。</p>
      </div>

      <!-- 報名區塊 -->
      <div class="register-section" v-else-if="!isRegistered">
        <label class="confirm-label">
          <input type="checkbox" v-model="confirmed">
          本人確定要報名此活動，並確定能如期到場參加活動。如不克前往，將會回報名系統註銷報名。
        </label>
        <button
          class="register-btn"
          :disabled="!confirmed || registering"
          @click="register"
        >
          <span v-if="registering">報名中...</span>
          <span v-else>確認報名</span>
        </button>
      </div>

      <!-- 已報名 -->
      <div class="registered-section" v-else>
        <p class="registered-text">您已報名此活動</p>
        <button class="cancel-btn" @click="cancelRegistration" :disabled="cancelling || isActivityEnded">
          {{ cancelling ? '取消中...' : (isActivityEnded ? '活動已結束，無法取消報名' : '取消報名') }}
        </button>
      </div>

      <!-- 返回按鈕 -->
      <div class="back-section">
        <button class="back-btn" @click="$router.go(-1)">返回</button>
      </div>
    </div>
  </div>

  <!-- 載入中 -->
  <div v-else-if="loading" class="state-page">
    <div class="state-card">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>
  </div>

  <!-- 錯誤 -->
  <div v-else-if="error" class="state-page">
    <div class="state-card error-card">
      <p>{{ error }}</p>
      <button class="back-btn" @click="$router.go(-1)">返回</button>
    </div>
  </div>
</template>

<script>
import { getActivityById, registerActivity, cancelRegistration as cancelReg, getUserActivities, getActivityAnnouncement } from '@/api.js'
import { categoryLabel } from '@/utils/category.js'

export default {
  name: 'ActivityDetail',
  data() {
    return {
      activity: null,
      loading: true,
      error: null,
      confirmed: false,
      registering: false,
      cancelling: false,
      isRegistered: false,
      announcement: ''
    }
  },
  async created() {
    await this.loadActivity()
    await this.checkRegistrationStatus()
    try {
      const res = await getActivityAnnouncement()
      this.announcement = res.data.announcement || ''
    } catch (e) { /* 忽略，保持空白 */ }
  },
  computed: {
    // 消毒後的活動簡述，供 v-html 安全渲染（內容由活動建立者輸入）
    safeDescription() {
      return this.sanitizeHtml(this.activity && this.activity.description)
    },
    // 是否已額滿（有設人數上限且已達上限才算；未設上限＝不限）
    isFull() {
      const max = parseInt(this.activity && this.activity.max_participants, 10)
      if (!max || max <= 0) return false
      return (this.activity.registration_count || 0) >= max
    },
    // 報名截止時間（reg_deadline；未設則以活動開始時間為截止）
    effectiveDeadline() {
      const d = (this.activity && (this.activity.reg_deadline || this.activity.start_date)) || null
      if (!d) return null
      const dt = new Date(d)
      return isNaN(dt.getTime()) ? null : dt
    },
    isRegistrationClosed() {
      return !!this.effectiveDeadline && new Date() > this.effectiveDeadline
    },
    // 活動是否已結束（以結束時間為準，無結束則用開始時間）
    isActivityEnded() {
      const d = (this.activity && (this.activity.end_date || this.activity.start_date)) || null
      if (!d) return false
      const dt = new Date(d)
      return !isNaN(dt.getTime()) && new Date() > dt
    },
    formatDeadline() {
      const dt = this.effectiveDeadline
      if (!dt) return '—'
      return dt.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
    }
  },
  methods: {
    // HTML 消毒：只保留編輯器會產生的安全標籤/屬性，過濾 script、on*、img、javascript: 等
    sanitizeHtml(html) {
      if (!html) return ''
      const allowed = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'A', 'SPAN', 'DIV', 'P', 'BR', 'FONT', 'UL', 'OL', 'LI'])
      let doc
      try {
        doc = new DOMParser().parseFromString(String(html), 'text/html')
      } catch (e) {
        return ''
      }
      const clean = (node) => {
        for (const child of Array.from(node.childNodes)) {
          if (child.nodeType === 3) continue          // 文字節點保留
          if (child.nodeType !== 1) { child.remove(); continue }  // 註解等移除
          const tag = child.tagName
          if (!allowed.has(tag)) {
            // 不允許的標籤（script/img/iframe…）以純文字取代，去除危險元素
            child.replaceWith(document.createTextNode(child.textContent || ''))
            continue
          }
          for (const attr of Array.from(child.attributes)) {
            const name = attr.name.toLowerCase()
            const val = attr.value
            if (name === 'style') {
              const safe = val.split(';').map(s => s.trim())
                .filter(s => /^(text-align|color)\s*:/i.test(s) && !/url\s*\(|expression|javascript:/i.test(s))
              if (safe.length) child.setAttribute('style', safe.join('; '))
              else child.removeAttribute('style')
              continue
            }
            let keep = false
            if (tag === 'A' && name === 'href') keep = /^(https?:|mailto:)/i.test(val.trim())
            else if (tag === 'A' && (name === 'target' || name === 'rel')) keep = true
            else if (tag === 'FONT' && name === 'color') keep = true
            if (!keep) child.removeAttribute(attr.name)
          }
          if (tag === 'A') {
            child.setAttribute('target', '_blank')
            child.setAttribute('rel', 'noopener noreferrer')
          }
          clean(child)
        }
      }
      clean(doc.body)
      return doc.body.innerHTML
    },
    async loadActivity() {
      const id = this.$route.params.id
      try {
        this.loading = true
        this.error = null
        const response = await getActivityById(id)
        this.activity = response.data
      } catch (err) {
        console.error('載入活動詳情失敗：', err)
        if (err.response?.status === 404) {
          this.error = '找不到此活動'
        } else {
          this.error = '無法載入活動詳情，請稍後再試'
        }
      } finally {
        this.loading = false
      }
    },

    async checkRegistrationStatus() {
      const token = localStorage.getItem('access_token')
      if (!token || !this.activity) return
      try {
        const response = await getUserActivities()
        const registeredIds = response.data.map(act => act.id)
        this.isRegistered = registeredIds.includes(this.activity.id)
      } catch (err) {
        console.error('檢查報名狀態失敗：', err)
      }
    },

    formatTimeRange(start, end) {
      if (!start) return '待定'
      const s = new Date(start)
      const parts = {
        date: s.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }),
        startTime: s.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })
      }
      if (end) {
        const e = new Date(end)
        const sameDay = s.toDateString() === e.toDateString()
        const endTime = e.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })
        if (sameDay) {
          return `${parts.date} ${parts.startTime}~${endTime}`
        }
        const endDate = e.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })
        return `${parts.date} ${parts.startTime} ~ ${endDate} ${endTime}`
      }
      return `${parts.date} ${parts.startTime}`
    },

    formatCategory(cat) {
      return categoryLabel(cat, '—')
    },

    formatRegTime(dt) {
      if (!dt) return ''
      try {
        const d = new Date(dt)
        return d.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })
      } catch {
        return dt
      }
    },

    async register() {
      const token = localStorage.getItem('access_token')
      if (!token) {
        alert('請先登入才能報名活動')
        this.$router.push('/login')
        return
      }
      try {
        this.registering = true
        await registerActivity(this.activity.id)
        alert('報名成功！')
        this.isRegistered = true
        // 重新載入以更新報名人數
        await this.loadActivity()
      } catch (err) {
        alert('報名失敗：' + (err.response?.data?.detail || '未知錯誤'))
      } finally {
        this.registering = false
      }
    },

    async cancelRegistration() {
      if (!confirm('確定要取消報名嗎？')) return
      try {
        this.cancelling = true
        await cancelReg(this.activity.id)
        alert('已取消報名')
        this.isRegistered = false
        await this.loadActivity()
      } catch (err) {
        alert('取消報名失敗：' + (err.response?.data?.detail || '未知錯誤'))
      } finally {
        this.cancelling = false
      }
    }
  }
}
</script>

<style scoped>
.detail-page {
  max-width: 900px;
  margin: 30px auto;
  padding: 0 20px;
}

.reflection-bonus {
  color: #2e7d32;
  font-size: 14px;
}

.detail-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* 標題區 */
.detail-header {
  background: linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%);
  padding: 20px 30px;
}

.detail-header h2 {
  color: #fff;
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

/* 資訊表格 */
.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.detail-table th,
.detail-table td {
  border: 1px solid #dee2e6;
  padding: 12px 16px;
  text-align: left;
  vertical-align: top;
}

.detail-table th {
  background: #f0f7f2;
  color: #1a5c2e;
  font-weight: 600;
  width: 120px;
  white-space: nowrap;
}

.detail-table td {
  color: #333;
}

.detail-table td a {
  color: #1a5c2e;
  word-break: break-all;
}

.description-cell {
  line-height: 1.7;
  white-space: pre-wrap;
}

/* 活動公告 */
.announcement {
  margin: 0;
  padding: 20px 30px;
  background: #fffbeb;
  border-top: 1px solid #dee2e6;
}

.announcement h3 {
  color: #b45309;
  font-size: 16px;
  margin: 0 0 8px;
}

.announcement p {
  color: #78350f;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* 報名情形 */
.registration-info {
  padding: 20px 30px;
  border-top: 1px solid #dee2e6;
}

.registration-info h3 {
  color: #1a5c2e;
  font-size: 16px;
  margin: 0 0 8px;
}

.registration-info p {
  font-size: 15px;
  color: #333;
  margin: 0;
}

/* 報名區 */
.register-section {
  padding: 20px 30px 30px;
  border-top: 1px solid #dee2e6;
}

.confirm-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  cursor: pointer;
  margin-bottom: 16px;
}

.confirm-label input[type="checkbox"] {
  margin-top: 4px;
  accent-color: #1a5c2e;
  width: 18px;
  height: 18px;
}

.reg-deadline {
  margin: 6px 0 0;
  color: #555;
}
.full-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 10px;
  background: #c62828;
  color: #fff;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}
.full-text {
  margin: 0;
  padding: 14px;
  background: #fdecea;
  border: 1px solid #f5c6cb;
  color: #b71c1c;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
}
.register-btn {
  display: block;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 14px 24px;
  background: linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(26, 92, 46, 0.3);
}

.register-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(26, 92, 46, 0.4);
}

.register-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* 已報名 */
.registered-section {
  padding: 20px 30px 30px;
  border-top: 1px solid #dee2e6;
  text-align: center;
}

.registered-text {
  color: #1a5c2e;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 12px;
}

.cancel-btn {
  padding: 10px 24px;
  background: #fff;
  color: #dc3545;
  border: 2px solid #dc3545;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn:hover:not(:disabled) {
  background: #dc3545;
  color: #fff;
}

.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 返回 */
.back-section {
  padding: 16px 30px 24px;
  text-align: center;
}

.back-btn {
  padding: 10px 30px;
  background: #f7fafc;
  color: #4a5568;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

/* 載入中 / 錯誤狀態 */
.state-page {
  max-width: 500px;
  margin: 80px auto;
  padding: 0 20px;
  text-align: center;
}

.state-card {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.error-card p {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #1a5c2e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 響應式 */
@media (max-width: 640px) {
  .detail-page {
    margin: 16px auto;
  }

  .detail-header {
    padding: 16px 20px;
  }

  .detail-header h2 {
    font-size: 18px;
  }

  .detail-table th,
  .detail-table td {
    padding: 10px 12px;
    font-size: 14px;
  }

  .detail-table th {
    width: 90px;
  }

  /* 手機版第一、二行改為 2 欄 */
  .detail-table tr:nth-child(1) th:nth-child(3),
  .detail-table tr:nth-child(1) td:nth-child(4),
  .detail-table tr:nth-child(2) th:nth-child(3),
  .detail-table tr:nth-child(2) td:nth-child(4) {
    display: none;
  }

  .announcement,
  .registration-info,
  .register-section,
  .registered-section,
  .back-section {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
